import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Package } from "lucide-react";

export const ProductCard = ({ product }) => {
  const isBundle = !!product.is_bundle;
  const hasDiscount = product.original_price && product.original_price > product.price;

  return (
    <Link
      to={`/products/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className={`group relative flex flex-col rounded-3xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${
        isBundle
          ? "border-[#1565FF]/40 bg-[#0f1a30] hover:border-[#1565FF] hover:shadow-lab-glow-lg"
          : "border-[#2A2A2A] bg-[#171717] hover:border-[#1565FF]/60 hover:shadow-lab-glow"
      }`}
    >
      <div className="relative aspect-[4/5] vial-shell overflow-hidden">
        <div className="absolute inset-0 grid-backdrop opacity-30" />

        {isBundle ? (
          <>
            {/* Multi-vial layout for bundles (2 or 3 vials) */}
            <div className={`absolute inset-0 flex items-center justify-center px-6 ${(product.includes || []).length >= 3 ? "gap-2" : "gap-3"}`}>
              {(product.includes || ["", ""]).map((_line, n, arr) => {
                const total = arr.length;
                const isCenter = total === 3 && n === 1;
                const rot = total === 3
                  ? (n === 0 ? -6 : n === 2 ? 6 : 0)
                  : (n === 0 ? -3 : 3);
                const ty = total === 3
                  ? (isCenter ? 0 : n === 0 ? 8 : -8)
                  : (n === 0 ? 6 : -6);
                const w = total === 3 ? "w-[28%]" : "w-[38%]";
                return (
                  <div
                    key={n}
                    className={`relative ${w} h-[74%] rounded-t-[12px] rounded-b-[6px] border border-[#2A2A2A] bg-[#0e0e0e] overflow-hidden`}
                    style={{ transform: `translateY(${ty}px) rotate(${rot}deg)` }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[14%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
                    <div className="absolute top-[14%] left-0 right-0 h-[6%] bg-[#171717] border-b border-[#2A2A2A]" />
                    <div className="absolute left-0 right-0 bottom-0 h-[58%] vial-liquid" />
                    <div className="absolute left-0 right-0 bottom-[56%] h-[1.5px] bg-[#1565FF]/70 blur-[2px]" />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[46%] h-[78%] rounded-t-[14px] rounded-b-[6px] border border-[#2A2A2A] bg-[#0e0e0e] overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[14%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
            <div className="absolute top-[14%] left-0 right-0 h-[6%] bg-[#171717] border-b border-[#2A2A2A]" />
            <div className="absolute left-0 right-0 bottom-0 h-[58%] vial-liquid" />
            <div className="absolute left-1/2 -translate-x-1/2 top-[32%] w-[86%] px-2 py-2 bg-[#0B0B0B]/85 border border-[#2A2A2A] rounded-[6px]">
              <div className="label-mono text-[8.5px] text-[#6E7784]">Lot 24-{product.id.toUpperCase()}</div>
              <div className="mt-1 text-white text-[11px] font-medium tracking-tight leading-tight">{product.name}</div>
              <div className="mt-0.5 text-[#B7BCC7] text-[9.5px]">{product.strength}</div>
            </div>
            <div className="absolute left-0 right-0 bottom-[56%] h-[2px] bg-[#1565FF]/70 blur-[2px]" />
          </div>
        )}

        {/* Top-right badge */}
        {isBundle ? (
          <div className="absolute top-4 right-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#1565FF] text-white">
            <Package size={11} />
            <span className="text-[10px] font-mono tracking-widest uppercase">Bundle</span>
          </div>
        ) : (
          <div className="absolute top-4 right-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0B0B0B]/80 border border-[#1565FF]/40">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1565FF]" />
            <span className="text-[10px] font-mono tracking-widest text-white">{product.purity}</span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3 border-t border-[#2A2A2A]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-mono">{isBundle ? "Starter Kit" : product.category.replace("-", " · ")}</div>
            <div className="mt-1.5 text-white font-sans font-medium tracking-tight text-[17px]">{product.name}</div>
          </div>
          <div className="text-right">
            {hasDiscount && (
              <div className="text-[11px] text-[#6E7784] line-through font-mono">
                £{product.original_price.toFixed(2)}
              </div>
            )}
            <div className="text-white font-mono text-[13px] tabular-nums">
              £{product.price.toFixed(2)}
            </div>
          </div>
        </div>
        <p className="text-[#B7BCC7] text-[13px] leading-relaxed line-clamp-2">
          {product.short_description}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-[#6E7784] font-mono tracking-widest uppercase">
            {product.strength}
          </span>
          <span className="inline-flex items-center gap-1 text-[13px] text-white opacity-80 group-hover:opacity-100 group-hover:text-[#1565FF] transition-colors">
            View details <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
};
